import { type HeadingLevel } from '@canvas/adf';

/**
 * NOTE: this should be the same as the `letter-spacing` value set in
 * @see {file://../../../services/canvas/src/app.css}. Used to align
 * the way in which text is rendered in Editor + WebGL.
 */
export const DEFAULT_KERNING = -0.01;

export type GlyphTextContainerType = 'paragraph';
export type GlyphHeadingContainerType = 'heading';

const listContainerTypes = [
  'orderedList',
  'bulletList',
  'orderedListItem',
  'bulletListItem',
] as const;
export type GlyphListContainerType = (typeof listContainerTypes)[number];

export type GlyphDecorativeType =
  | 'matchActive'
  | 'match'
  | 'mention'
  | 'date'
  | 'emoji'
  | 'bulletListMarker'
  | 'orderedListMarker';

export type GlyphContainerType =
  | GlyphTextContainerType
  | GlyphHeadingContainerType
  | GlyphListContainerType
  | GlyphDecorativeType;

export const containerSelectors: Record<
  GlyphTextContainerType | GlyphListContainerType,
  string
> &
  Record<GlyphHeadingContainerType, Record<HeadingLevel, string>> = {
  paragraph: 'p',
  bulletList: 'ul',
  bulletListItem: 'ul-li',
  orderedList: 'ol',
  orderedListItem: 'ol-li',
  heading: {
    1: 'h1',
    2: 'h2',
    3: 'h2',
    4: 'h4',
    5: 'h5',
    6: 'h6',
  },
};

export enum SpacingType {
  default = 'defaultTextSpacing',
  relaxed = 'relaxedTextSpacing',
}

export type TextContainerTypeMap<T> = Record<GlyphTextContainerType, T> &
  Record<GlyphHeadingContainerType, Record<HeadingLevel, T>>;

export type ListContainerTypeMap<T> = Record<GlyphListContainerType, T>;

export type SpacingTypeMap<T> = Record<SpacingType, T>;

/**
 * Types for text styles related to the physical size of text and lines of text
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const textSizeStyles = ['font-size', 'line-height'] as const;
export type TextSizeVarName = `${string}-${(typeof textSizeStyles)[number]}`;
export type TextSizeVarValue = { value: number; units?: 'px' };
export type TextSizeVarMap = Record<TextSizeVarName, TextSizeVarValue>;

export const textSizeStylesMap: SpacingTypeMap<TextContainerTypeMap<TextSizeVarMap>> = {
  defaultTextSpacing: {
    paragraph: {
      'p-font-size': { value: 11.6 / 0.75, units: 'px' },
      'p-line-height': { value: 22, units: 'px' },
    },
    heading: {
      1: {
        'h1-font-size': { value: 27, units: 'px' },
        'h1-line-height': { value: 32, units: 'px' },
      },
      2: {
        'h2-font-size': { value: 23, units: 'px' },
        'h2-line-height': { value: 27, units: 'px' },
      },
      3: {
        'h3-font-size': { value: 18, units: 'px' },
        'h3-line-height': { value: 23, units: 'px' },
      },
      4: {
        'h4-font-size': { value: 16, units: 'px' },
        'h4-line-height': { value: 23, units: 'px' },
      },
      5: {
        'h5-font-size': { value: 14, units: 'px' },
        'h5-line-height': { value: 18, units: 'px' },
      },
      6: {
        'h6-font-size': { value: 13, units: 'px' },
        'h6-line-height': { value: 18, units: 'px' },
      },
    },
  },
  relaxedTextSpacing: {
    paragraph: {
      'p-font-size': { value: 14, units: 'px' },
      'p-line-height': { value: 20, units: 'px' },
    },
    heading: {
      1: {
        'h1-font-size': { value: 24, units: 'px' },
        'h1-line-height': { value: 28, units: 'px' },
      },
      2: {
        'h2-font-size': { value: 20, units: 'px' },
        'h2-line-height': { value: 24, units: 'px' },
      },
      3: {
        'h3-font-size': { value: 16, units: 'px' },
        'h3-line-height': { value: 20, units: 'px' },
      },
      4: {
        'h4-font-size': { value: 14, units: 'px' },
        'h4-line-height': { value: 16, units: 'px' },
      },
      5: {
        'h5-font-size': { value: 12, units: 'px' },
        'h5-line-height': { value: 16, units: 'px' },
      },
      6: {
        'h6-font-size': { value: 11, units: 'px' },
        'h6-line-height': { value: 16, units: 'px' },
      },
    },
  },
};

/**
 * Types for text styles related to 'marks' in adf
 */
type FontWeight =
  | { name: 'regular'; value: 400 }
  | { name: 'medium'; value: 500 }
  | { name: 'semi-bold'; value: 600 };

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const textMarkStyles = ['font-weight'] as const;
export type TextMarkVarName = `${string}-${(typeof textMarkStyles)[number]}`;
export type TextMarkVarValue = FontWeight;
export type TextMarkVarMap = Record<TextMarkVarName, TextMarkVarValue>;

export const textMarkStylesMap: TextContainerTypeMap<TextMarkVarMap> = {
  paragraph: {
    'p-font-weight': { name: 'regular', value: 400 },
  },
  heading: {
    1: {
      'h1-font-weight': { name: 'medium', value: 500 },
    },
    2: {
      'h2-font-weight': { name: 'medium', value: 500 },
    },
    3: {
      'h3-font-weight': { name: 'semi-bold', value: 600 },
    },
    4: {
      'h4-font-weight': { name: 'semi-bold', value: 600 },
    },
    5: {
      'h5-font-weight': { name: 'semi-bold', value: 600 },
    },
    6: {
      'h6-font-weight': { name: 'semi-bold', value: 600 },
    },
  },
};

/**
 * Types for styles defining the spacing around certain element types
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const spacingStyles = ['margin-top', 'padding-left'] as const;
export type SpacingVarName = `${string}-${(typeof spacingStyles)[number]}`;
export type SpacingVarValue = { value: number; units?: 'px' };
export type SpacingVarMap = Record<SpacingVarName, SpacingVarValue>;

export const spacingStylesMap: SpacingTypeMap<
  TextContainerTypeMap<SpacingVarMap> & ListContainerTypeMap<SpacingVarMap>
> = {
  defaultTextSpacing: {
    paragraph: {
      'p-margin-top': { value: 0 },
      'p-under-h-margin-top': { value: 6, units: 'px' },
      'p-padding-left': { value: 0 },
    },
    orderedList: {
      'ol-margin-top': { value: 0 },
      'ol-padding-left': { value: 12, units: 'px' },
    },
    orderedListItem: {
      'ol-li-margin-top': { value: 0 },
      'ol-li-padding-left': { value: 0, units: 'px' },
    },
    bulletList: {
      'ul-margin-top': { value: 0 },
      'ul-padding-left': { value: 6, units: 'px' },
    },
    bulletListItem: {
      'ul-li-margin-top': { value: 0 },
      'ul-li-padding-left': { value: 6, units: 'px' },
    },
    heading: {
      1: {
        'h1-margin-top': { value: 12, units: 'px' },
        'h1-padding-left': { value: 0 },
      },
      2: {
        'h2-margin-top': { value: 11, units: 'px' },
        'h2-padding-left': { value: 0 },
      },
      3: {
        'h3-margin-top': { value: 10, units: 'px' },
        'h3-padding-left': { value: 0 },
      },
      4: {
        'h4-margin-top': { value: 8.5, units: 'px' },
        'h4-padding-left': { value: 0 },
      },
      5: {
        'h5-margin-top': { value: 7.5, units: 'px' },
        'h5-padding-left': { value: 0 },
      },
      6: {
        'h6-margin-top': { value: 7.5, units: 'px' },
        'h6-padding-left': { value: 0 },
      },
    },
  },
  relaxedTextSpacing: {
    paragraph: {
      'p-margin-top': { value: 12, units: 'px' },
      'p-under-h-margin-top': { value: 12, units: 'px' },
      'p-padding-left': { value: 0 },
    },
    orderedList: {
      'ol-margin-top': { value: 10, units: 'px' },
      'ol-padding-left': { value: 24, units: 'px' },
    },
    orderedListItem: {
      'ol-li-margin-top': { value: 4, units: 'px' },
      'ol-li-padding-left': { value: 6, units: 'px' },
    },
    bulletList: {
      'ul-margin-top': { value: 10, units: 'px' },
      'ul-padding-left': { value: 24, units: 'px' },
    },
    bulletListItem: {
      'ul-li-margin-top': { value: 4, units: 'px' },
      'ul-li-padding-left': { value: 6, units: 'px' },
    },
    heading: {
      1: {
        'h1-margin-top': { value: 40, units: 'px' },
        'h1-padding-left': { value: 0 },
      },
      2: {
        'h2-margin-top': { value: 32, units: 'px' },
        'h2-padding-left': { value: 0 },
      },
      3: {
        'h3-margin-top': { value: 24, units: 'px' },
        'h3-padding-left': { value: 0 },
      },
      4: {
        'h4-margin-top': { value: 20, units: 'px' },
        'h4-padding-left': { value: 0 },
      },
      5: {
        'h5-margin-top': { value: 20, units: 'px' },
        'h5-padding-left': { value: 0 },
      },
      6: {
        'h6-margin-top': { value: 20, units: 'px' },
        'h6-padding-left': { value: 0 },
      },
    },
  },
};

const getCSSVarName = (name: TextSizeVarName | TextMarkVarName | SpacingVarName) =>
  `--canvas-${name}`;

export const setParagraphStyles = (spacing: SpacingType) => {
  const textSizes = textSizeStylesMap[spacing].paragraph;
  const textMarks = textMarkStylesMap.paragraph;
  const textSpacing = spacingStylesMap[spacing].paragraph;

  for (const style in textSizes) {
    const cssVarName = getCSSVarName(style as TextSizeVarName);
    const value = textSizes[style as TextSizeVarName];

    const units = value?.units ?? '';

    globalThis.document.documentElement.style.setProperty(
      cssVarName,
      `${value.value}${units}`,
    );
  }

  for (const style in textMarks) {
    const cssVarName = getCSSVarName(style as TextMarkVarName);
    const value = textMarks[style as TextMarkVarName];

    globalThis.document.documentElement.style.setProperty(cssVarName, `${value.value}`);
  }

  for (const style in textSpacing) {
    const cssVarName = getCSSVarName(style as SpacingVarName);
    const value = textSpacing[style as SpacingVarName];

    const units = value?.units ?? '';

    globalThis.document.documentElement.style.setProperty(
      cssVarName,
      `${value.value}${units}`,
    );
  }
};

export const setHeadingStyles = (spacing: SpacingType) => {
  for (let l = 1; l <= 6; l++) {
    const level = l as HeadingLevel;

    const textSizes = textSizeStylesMap[spacing].heading[level];
    const textMarks = textMarkStylesMap.heading[level];
    const textSpacing = spacingStylesMap[spacing].heading[level];

    for (const style in textSizes) {
      const cssVarName = getCSSVarName(style as TextSizeVarName);
      const value = textSizes[style as TextSizeVarName];

      const units = value?.units ?? '';

      globalThis.document.documentElement.style.setProperty(
        cssVarName,
        `${value.value}${units}`,
      );
    }

    for (const style in textMarks) {
      const cssVarName = getCSSVarName(style as TextMarkVarName);
      const value = textMarks[style as TextMarkVarName];

      globalThis.document.documentElement.style.setProperty(cssVarName, `${value.value}`);
    }

    for (const style in textSpacing) {
      const cssVarName = getCSSVarName(style as SpacingVarName);
      const value = textSpacing[style as SpacingVarName];

      const units = value?.units ?? '';

      globalThis.document.documentElement.style.setProperty(
        cssVarName,
        `${value.value}${units}`,
      );
    }
  }
};

export const setListStyles = (spacing: SpacingType) => {
  const spacingMap = spacingStylesMap[spacing];

  for (const listType of listContainerTypes) {
    const listSpacing = spacingMap[listType];

    for (const style in listSpacing) {
      const cssVarName = getCSSVarName(style as SpacingVarName);
      const value = listSpacing[style as SpacingVarName];

      const units = value?.units ?? '';

      globalThis.document.documentElement.style.setProperty(
        cssVarName,
        `${value.value}${units}`,
      );
    }
  }
};

export const setKerning = () => {
  globalThis.document.documentElement.style.setProperty(
    '--canvas-default-kerning',
    `${DEFAULT_KERNING}em`,
  );
};

export const DEFAULT_FONT_SCALE = 1;
// Potentially need a MinimumFontScaleComponent in the event that we need
// per-entity minimum font scaling
export const MINIMUM_FONT_SCALE = 1;

/**
 * Normalizes a fontScale value to a safe positive number.
 * Returns `DEFAULT_FONT_SCALE` for NaN, Infinity, zero, or negative values.
 */
export const normalizeFontScale = (fontScale: number): number => {
  if (!Number.isFinite(fontScale) || fontScale <= 0) {
    return DEFAULT_FONT_SCALE;
  }
  return fontScale;
};

export const MINIMUM_FALLBACK_HEIGHT = 6;

// As per above, this is the size of 'normal' text, in points.
// 11.6 points = 11.6 / 0.75 pixels, since 1pt = 0.75px
export const DEFAULT_FONT_SIZE = 11.6 / 0.75;

export const fontSizePresetOrder = [
  'small',
  'normal',
  'medium',
  'large',
  'xLarge',
  'huge',
] as const;

export type FontSizePresetId = (typeof fontSizePresetOrder)[number];

export const fontSizePresetPxMap: Record<FontSizePresetId, number> = {
  small: 11,
  normal: Math.round(DEFAULT_FONT_SCALE * DEFAULT_FONT_SIZE),
  medium: 20,
  large: 28,
  xLarge: 36,
  huge: 48,
};

export const MINIMUM_FONT_SIZE_PX = 12;
export const FONT_SIZE_STEP_PX = 1;
export const FONT_SIZE_LARGE_STEP_PX = 4;

/**
 * Applies a minimum floor of `MINIMUM_FONT_SIZE_PX` (12). No upper bound.
 */
export const normalizeFontSizePx = (fontSizePx: number) => {
  return Math.max(MINIMUM_FONT_SIZE_PX, Math.round(fontSizePx));
};

export type FontSizeTextLevel = 0 | HeadingLevel;

export const getFontSizeBasePxForTextLevel = (
  textLevel: FontSizeTextLevel = 0,
  spacing: SpacingType = SpacingType.default,
): number => {
  if (textLevel === 0) {
    return textSizeStylesMap[spacing].paragraph['p-font-size'].value;
  }

  return textSizeStylesMap[spacing].heading[textLevel][`h${textLevel}-font-size`].value;
};

export const fontScaleToFontSizePx = (fontScale = DEFAULT_FONT_SCALE): number => {
  return Math.round(fontScale * DEFAULT_FONT_SIZE);
};

export const fontSizePxToFontScale = (fontSizePx: number): number => {
  const normalizedFontSizePx = normalizeFontSizePx(fontSizePx);

  if (normalizedFontSizePx === fontSizePresetPxMap.normal) {
    return DEFAULT_FONT_SCALE;
  }

  return normalizedFontSizePx / DEFAULT_FONT_SIZE;
};

export const fontScaleToEffectiveFontSizePx = (
  fontScale = DEFAULT_FONT_SCALE,
  textLevel: FontSizeTextLevel = 0,
  spacing: SpacingType = SpacingType.default,
): number => {
  return Math.round(fontScale * getFontSizeBasePxForTextLevel(textLevel, spacing));
};

export const effectiveFontSizePxToFontScale = (
  fontSizePx: number,
  textLevel: FontSizeTextLevel = 0,
  spacing: SpacingType = SpacingType.default,
): number => {
  const normalizedFontSizePx = normalizeFontSizePx(fontSizePx);
  const baseFontSizePx = getFontSizeBasePxForTextLevel(textLevel, spacing);

  if (normalizedFontSizePx === Math.round(baseFontSizePx)) {
    return DEFAULT_FONT_SCALE;
  }

  return normalizedFontSizePx / baseFontSizePx;
};

export const getFontSizePresetId = (fontSizePx: number): FontSizePresetId | undefined => {
  return fontSizePresetOrder.find(
    (presetId) => fontSizePresetPxMap[presetId] === fontSizePx,
  );
};

/**
 * Computes the height of a text fallback for a given line height and font scale
 *
 * Note: We ideally want 12px (font scaled) between each line of fallback
 * roundedRects, BUT, we never want the fallback roundedRects to have a height
 * less than MINIMUM_FALLBACK_HEIGHT
 *
 * @param lineHeight the rendered lineHeight at the given font scale
 * @param fontScale the current font scale
 * @returns the height of the fallback
 */
export const fallbackHeight = (lineHeight: number, fontScale = 1): number => {
  const gap = fontScale * 12;
  const minFallbackHeight = fontScale * MINIMUM_FALLBACK_HEIGHT;

  return Math.max(lineHeight - gap, minFallbackHeight);
};

/**
 * Computes the corner radius of a text fallback for a given line height and font scale
 *
 * @param lineHeight the rendered lineHeight at the given font scale
 * @param fontScale the current font scale
 * @returns the height of the fallback
 */
export const fallbackRadius = (lineHeight: number, fontScale = 1): number => {
  const height = fallbackHeight(lineHeight, fontScale);

  return height / 2;
};

/**
 * Computes the amount by which left aligned text is offset from the center of
 * the text area
 */
export const leftAlignOffsetFromCentre = (maxWidth: number, textWidth: number) =>
  (maxWidth - textWidth) / 2;

export const DEFAULT_TEXT_COLOR = 'palette.dark.gray.300';

/**
 * We do not have the itemization for blockquotes and code blocks in the Canvas editor
 * preset but it is parsed by proseMirror. These would be parsed as unsupported content.
 * When we add itemization for these tags, we can remove the tags from the list.
 */
export const UNSUPPORTED_BLOCK_TAGS = ['blockquote', 'pre'];
